// Запросы экрана «Настройки → MCP»: /api/mcp/info, /api/mcp/keys, /api/mcp/oauth-clients.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'

export type McpInfo = { mcp_url: string; resource_metadata_url: string }

export type ApiKey = {
  id: string
  name: string
  prefix: string
  created_at: string
  last_used_at: string | null
  revoked_at: string | null
}

/** Ответ на выпуск ключа: `secret` приходит один раз и больше нигде не показывается. */
export type CreatedKey = { key: ApiKey; secret: string }

export type OAuthGrant = {
  client_id: string
  client_name: string
  granted_at: string
  last_used_at: string | null
}

export const mcpKeys = {
  info: ['mcp', 'info'] as const,
  keys: ['mcp', 'keys'] as const,
  grants: ['mcp', 'oauth-clients'] as const,
}

export const mcpApi = {
  info: () => api.get<McpInfo>('/mcp/info'),
  keys: () => api.get<ApiKey[]>('/mcp/keys'),
  createKey: (name: string) => api.post<CreatedKey>('/mcp/keys', { name }),
  revokeKey: (id: string) => api.delete(`/mcp/keys/${id}`),
  grants: () => api.get<OAuthGrant[]>('/mcp/oauth-clients'),
  revokeGrant: (clientId: string) => api.delete(`/mcp/oauth-clients/${encodeURIComponent(clientId)}`),
}

export function useMcpInfo() {
  return useQuery({ queryKey: mcpKeys.info, queryFn: mcpApi.info, staleTime: Infinity })
}

export function useMcpKeys() {
  return useQuery({ queryKey: mcpKeys.keys, queryFn: mcpApi.keys })
}

export function useOAuthGrants() {
  return useQuery({ queryKey: mcpKeys.grants, queryFn: mcpApi.grants })
}

export function useCreateKey() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: mcpApi.createKey,
    onSettled: () => client.invalidateQueries({ queryKey: mcpKeys.keys }),
  })
}

export function useRevokeKey() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: mcpApi.revokeKey,
    onSettled: () => client.invalidateQueries({ queryKey: mcpKeys.keys }),
  })
}

export function useRevokeGrant() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: mcpApi.revokeGrant,
    onSettled: () => client.invalidateQueries({ queryKey: mcpKeys.grants }),
  })
}
